import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money, STATUS_LABELS } from "@/lib/api";
import { Badge, Card, toneFor, useAdmin } from "@/admin/AdminShell";

export function Overview() {
  const { user } = useAdmin();
  const [m, setM] = useState(null);
  const [integ, setInteg] = useState(null);
  const [s, setS] = useState(null);
  const load = () => Promise.all([api.get("/admin/metrics"), api.get("/admin/integrations"), api.get("/admin/settings")])
    .then(([a, b, c]) => { setM(a.data); setInteg(b.data); setS(c.data); });
  useEffect(() => { load(); }, []);
  if (!m || !integ || !s) return null;
  const stat = (label, value, testid) => <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}><p className="text-xs text-slate-500">{label}</p><p className="mt-1 text-2xl font-semibold">{value}</p></div>;
  const toggle = async (key, value) => {
    try { await api.put("/admin/checklist", { key, value }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <h1 className="text-2xl font-semibold">Overview</h1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stat("Paid orders", m.paid_orders, "metric-paid-orders")}
        {stat("Gross sales (USD)", money(m.gross_sales), "metric-gross-sales")}
        {stat("Refund rate", `${(m.refund_rate * 100).toFixed(1)}%`, "metric-refund-rate")}
        {stat("Revision rate", `${(m.revision_rate * 100).toFixed(1)}%`, "metric-revision-rate")}
        {stat("Attempts / delivered", m.attempts_per_delivered ?? "—", "metric-attempts")}
        {stat("Est. provider credits", `${m.estimated_provider_credits} (estimate)`, "metric-credits")}
        {stat("Avg. paid → delivered", m.avg_delivery_hours != null ? `${m.avg_delivery_hours} h` : "—", "metric-delivery-time")}
        {stat("Open alerts", m.open_alerts, "metric-alerts")}
      </div>
      <p className="text-xs text-slate-500">Payment fees are not available from the integration yet. Credit figures are estimates from Apiframe's published per-request cost; this dashboard does not compute profit.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Funnel (first-party events)" testid="admin-funnel">
          <ul className="space-y-1 text-sm">{Object.entries(m.funnel).map(([k, v]) => <li key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></li>)}</ul>
        </Card>
        <Card title="Paid orders by category / language">
          <ul className="space-y-1 text-sm">{Object.entries(m.by_category).map(([k, v]) => <li key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></li>)}</ul>
          <hr className="my-3" />
          <ul className="space-y-1 text-sm">{Object.entries(m.by_language).map(([k, v]) => <li key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></li>)}</ul>
        </Card>
        <Card title={`Integrations · ${integ.mode} mode`} testid="admin-integrations">
          <ul className="space-y-2 text-sm">
            <li>PayPal: <Badge tone={integ.paypal.configured ? "green" : "red"}>{integ.paypal.configured ? `configured (${integ.paypal.env})` : "not configured — simulator in dev"}</Badge> · webhook verification {integ.paypal.webhook_verification ? "on" : "off"}</li>
            <li>Apiframe: <Badge tone={integ.apiframe.configured ? "green" : "red"}>{integ.apiframe.configured ? "configured" : "not configured — dev sample tone"}</Badge> {integ.apiframe.model}</li>
            <li>Lyrics: <Badge tone={integ.lyrics.configured ? "green" : "red"}>{integ.lyrics.configured ? integ.lyrics.model : "manual drafting"}</Badge></li>
            <li>Email: <Badge tone={integ.email.configured ? "green" : "amber"}>{integ.email.mode}</Badge></li>
            <li>Storage: <Badge tone="red">not durable</Badge> {integ.storage.note}</li>
          </ul>
          <p className="mt-3 text-xs text-slate-500">Production blockers: {integ.production_blockers.length ? integ.production_blockers.join(", ") : "none"}</p>
        </Card>
        <Card title="Launch approval checklist" testid="admin-launch-checklist">
          <ul className="space-y-2 text-sm">
            {Object.entries(s.checklist_labels).map(([k, label]) => (
              <li key={k}><label className="flex items-center gap-2">
                <input type="checkbox" disabled={user.role !== "owner"} checked={!!s.launch_checklist[k]} onChange={(e) => toggle(k, e.target.checked)} data-testid={`checklist-${k}`} />{label}
              </label></li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">Items are only marked by a person. Nothing here is completed automatically.</p>
        </Card>
      </div>
    </div>
  );
}

export function Orders() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ q: "", payment_status: "", fulfillment_status: "", language_id: "", occasion_id: "" });
  const [langs, setLangs] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/admin/languages").then((r) => setLangs(r.data.languages)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/orders", { params: f }).then((r) => setRows(r.data)), 250);
    return () => clearTimeout(t);
  }, [f]);
  const sel = (k, opts, label) => (
    <select aria-label={label} className="rounded-lg border px-3 py-2 text-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-filter-${k}`}>
      <option value="">{label}: all</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Orders</h1>
      <div className="flex flex-wrap gap-2">
        <input className="rounded-lg border px-3 py-2 text-sm" placeholder="Search reference, email, recipient" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="admin-orders-search" />
        {sel("payment_status", ["not_started", "pending", "completed", "cancelled", "failed", "refunded"].map((x) => [x, x]), "Payment")}
        {sel("fulfillment_status", Object.entries(STATUS_LABELS), "Fulfillment")}
        {sel("language_id", langs.map((l) => [l.id, l.english_name]), "Language")}
      </div>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>
            {["Reference", "Created", "Recipient", "Email", "Occasion", "Tone", "Language", "Payment", "Fulfillment", "Revision", "Price"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="cursor-pointer border-t hover:bg-slate-50" data-testid={`admin-order-row-${o.reference}`} onClick={(e) => e.target.tagName !== "A" && navigate(`/admin/orders/${o.id}`)}>
                <td className="px-3 py-2"><Link className="font-medium text-violet-700 underline" to={`/admin/orders/${o.id}`}>{o.reference}</Link>{o.mode === "development" && <Badge>dev</Badge>}</td>
                <td className="px-3 py-2 text-slate-500">{o.created_at.slice(0, 10)}</td>
                <td className="px-3 py-2">{o.recipient?.name}</td>
                <td className="px-3 py-2">{o.buyer_email || "—"}</td>
                <td className="px-3 py-2">{o.occasion_label}{o.custom_occasion_label && ` — ${o.custom_occasion_label}`}<div className="text-xs text-slate-400">{o.occasion_category}</div></td>
                <td className="px-3 py-2">{o.tone}</td>
                <td className="px-3 py-2">{o.song_language?.english_name}</td>
                <td className="px-3 py-2"><Badge tone={toneFor(o.payment_status)}>{o.payment_status}</Badge></td>
                <td className="px-3 py-2"><Badge tone={toneFor(o.fulfillment_status)}>{STATUS_LABELS[o.fulfillment_status] || o.fulfillment_status}</Badge>{o.attention_reasons?.length > 0 && <Badge tone="red">review</Badge>}</td>
                <td className="px-3 py-2">{o.revision_status}</td>
                <td className="px-3 py-2">{o.price ? money(o.price, o.currency) : "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-500">No orders match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
